import { useEffect, useState } from "react";
import { listAllLabels, type Label } from "@/api";

/**
 * 라벨 카탈로그 적재 seam. 제안이나 중복 판정이 필요한 컴포넌트가 이 훅으로 카탈로그를 받는다.
 *
 * 적재 자체는 `listAllLabels` 의 세션 캐시가 소유한다. 마운트마다 호출해도 네트워크 요청을
 * 다시 보내지 않고, 앱은 부팅 시 한 번 캐시를 채운다. 이 훅이 추가하는 것은 **적재 상태**뿐이다.
 * 소비처가 "카탈로그가 아직 없다" 와 "비어 있다" 를 구분해야 하기 때문이다:
 *
 * - 도착 전에 "일치하는 기존 라벨이 없습니다" 를 표시하면 사실이 아닌 말을 하게 된다.
 * - 중복 판정(`newLabelBlocker`)은 빈 카탈로그에서 경고 없이 통과해 같은 뜻의 라벨을 하나 더 만든다.
 *
 * 동기 스냅샷(`reservedAllowlistSnapshot` 류)을 쓰지 않는 이유: 그쪽은 렌더 중 동기로 읽는
 * 소비처가 있고, 값이 없을 때 쓸 fallback vocab 이 있다. 라벨 카탈로그는 둘 다 없다. 스냅샷이
 * 비면 재렌더를 일으킬 것이 없어서, 데이터가 도착한 뒤에도 화면이 계속 빈 채로 남는다.
 */
export type LabelCatalogStatus = "loading" | "ready" | "failed";

export type LabelCatalogState = {
  catalog: readonly Label[];
  status: LabelCatalogStatus;
};

export function useLabelCatalog(): LabelCatalogState {
  const [state, setState] = useState<LabelCatalogState>({ catalog: [], status: "loading" });

  useEffect(() => {
    let cancelled = false;
    listAllLabels()
      .then((labels) => {
        if (!cancelled) {
          setState({ catalog: labels, status: "ready" });
        }
      })
      .catch(() => {
        // 제안은 보조 기능이라 실패해도 입력은 그대로 열어 둔다. 다만 "없습니다" 로 단정하지
        // 않도록 실패를 ready 와 구분해 알린다.
        if (!cancelled) {
          setState({ catalog: [], status: "failed" });
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return state;
}
